import React, { useCallback, useContext } from 'react';
import AccordionSummary from '@mui/material/AccordionSummary';
import { Accordion, Card, Tooltip, Typography, Zoom, Grid } from '@mui/material';
import Div from '@jumbo/shared/Div';
import Button from '@mui/material/Button';
import styled from '@emotion/styled';
import Span from '@jumbo/shared/Span';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import { ApiService } from 'app/servicesTwo/ApiService';
import useSwalWrapper from '@jumbo/vendors/sweetalert2/hooks';
import formatDate from '../../../utils/formatDate';
import { Link, useLocation } from 'react-router-dom';
import { PermissionContext } from 'app/contexts/PermissionContext';
import { EllipsisTooltip } from 'app/components/EllipsisTooltip/EllipsisTooltip';

const Item = styled(Span)(({ theme }) => ({
    padding: theme.spacing(0, 1),
}));

export const LinkStyle = styled(Link)`
    padding: 4px 10px;
    background: #7352C7;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 4px;

    svg {
        color: white;
    }
`;

const MenorItem = ({ item, setMenores, menores }) => {
    const location = useLocation();
    const Swal = useSwalWrapper();
    const { hasPermission } = useContext(PermissionContext);

    const toast = (variant, message) => {
        const Toast = Swal.mixin({
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            onOpen: (toast) => {
                toast.addEventListener('mouseenter', Swal.stopTimer);
                toast.addEventListener('mouseleave', Swal.resumeTimer);
            },
        });
        Toast.fire({
            icon: variant,
            title: message,
            showCloseButton: true,
            closeButtonAriaLabel: 'Fechar',
        });
    };

    const modalAlert = (id) => {
        Swal.fire({
            title: 'Tem certeza que deseja excluir?',
            text: 'Não será possível reverter a ação!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sim!',
            cancelButtonText: 'Não!',
            reverseButtons: true,
        }).then((result) => {
            if (result.value) {
                deleteMenor(id);
            }
        });
    };

    function removeById(arr, id) {
        const idx = arr.findIndex((obj) => obj.menor_id === id);
        if (idx > -1) arr.splice(idx, 1);
        return arr;
    }

    const deleteMenor = useCallback(async (id) => {
        try {
            await ApiService.delete(`/menores/${id}`)
                .then(() => {
                    const updated = removeById([...menores], id);
                    setMenores(updated);
                    toast('success', 'Menor excluído com sucesso');
                })
                .catch((error) => {
                    const mensagem =
                        error?.response?.data?.error || 'Ocorreu um erro ao excluir';
                    Swal.fire({
                        title: 'Não foi possível excluir',
                        text: mensagem,
                        icon: 'error',
                    });
                });
        } catch (err) {
            toast('error', 'Ocorreu um erro');
        }
    }, [menores]);

    return (
        <Card sx={{ mb: 1 }}>
            <Accordion square sx={{ borderRadius: 2 }}>
                <AccordionSummary
                    aria-controls='menorItem-content'
                    id='menorItem-header'
                    sx={{
                        px: 3,
                        flexDirection: 'row-reverse',
                        '& .MuiAccordionSummary-content': {
                            alignItems: 'center',
                            '&.Mui-expanded': { margin: '12px 0' },
                        },
                        '.MuiAccordionSummary-expandIconWrapper': {
                            borderRadius: 1,
                            border: 1,
                            color: 'text.secondary',
                            borderColor: 'divider',
                            transform: 'none',
                            height: 28,
                            width: 28,
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 1,
                            '&.Mui-expanded': {
                                transform: 'none',
                                color: 'primary.main',
                                borderColor: 'primary.main',
                            },
                            '& svg': { fontSize: '1.25rem' },
                        },
                    }}
                >
                    <Grid container spacing={2} alignContent='center'>
                        <Grid item lg={3} md={4} xs={8} sx={{ flexShrink: 0, px: 1 }}>
                            <Typography fontSize='12px' variant='h6' color='text.secondary' mb={0.25}>
                                Nome do Menor
                            </Typography>
                            <EllipsisTooltip text={item?.pessoa?.nome} />
                        </Grid>

                        <Grid item lg={2} md={3} xs={4} sx={{ flexShrink: 0, px: 1 }}>
                            <Typography fontSize='12px' variant='h6' color='text.secondary' mb={0.25}>
                                CPF
                            </Typography>
                            <EllipsisTooltip text={item?.pessoa?.cpf || '-'} />
                        </Grid>

                        <Grid item lg={2} md={3} xs={4} sx={{ flexShrink: 0, px: 1 }}>
                            <Typography fontSize='12px' variant='h6' color='text.secondary' mb={0.25}>
                                Dt. Nasc.
                            </Typography>
                            <EllipsisTooltip
                                text={
                                    item?.pessoa?.data_nascimento
                                        ? formatDate(item.pessoa.data_nascimento)
                                        : '-'
                                }
                            />
                        </Grid>

                        <Grid item lg={3} md={3} xs={4} sx={{ flexShrink: 0, px: 1 }}>
                            <Typography fontSize='12px' variant='h6' color='text.secondary' mb={0.25}>
                                Responsável
                            </Typography>
                            <EllipsisTooltip text={item?.responsavel?.nome || '-'} />
                        </Grid>

                        <Grid item lg={2} md={2} xs={4} sx={{ ml: 'auto', flexDirection: 'row', display: 'flex' }}>
                            <Tooltip title='Visualizar' TransitionComponent={Zoom} arrow>
                                <Item sx={{ ml: 'auto' }}>
                                    <LinkStyle
                                        to={`/app/editar-menor/${item.menor_id}`}
                                        state={{ backUrl: location.pathname + location.search }}
                                        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                                    >
                                        <VisibilityIcon />
                                    </LinkStyle>
                                </Item>
                            </Tooltip>

                            <Tooltip title='Excluir' TransitionComponent={Zoom} arrow>
                                <Item>
                                    <Button
                                        sx={{ minWidth: 1 }}
                                        disableElevation
                                        variant='contained'
                                        size='small'
                                        color='secondary'
                                        disabled={!hasPermission('Menores', 'deletar')}
                                        onClick={() => modalAlert(item.menor_id)}
                                    >
                                        <DeleteIcon />
                                    </Button>
                                </Item>
                            </Tooltip>
                        </Grid>
                    </Grid>
                </AccordionSummary>
            </Accordion>
        </Card>
    );
};

export default MenorItem;
